import React, { useState, useEffect, useRef } from 'react';
import { useOutletContext, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { Label } from '@shared/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@shared/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@shared/components/ui/card';
import { Alert, AlertDescription } from '@shared/components/ui/alert';
import { useToast } from '@shared/components/ui/use-toast';
import { PageHeader } from '@/components/portal/PortalUI';
import { User, Camera, Upload, Check, AlertCircle, Building2, Phone, Mail, MapPin, Car } from 'lucide-react';
import { supabase } from '@shared/supabaseClient';

const PortalProfile = () => {
  const { session } = useOutletContext();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const fileInputRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [stream, setStream] = useState(null);
  const [sites, setSites] = useState([]);
  const [noVehicle, setNoVehicle] = useState(false);
  
  const [formData, setFormData] = useState({
    person_name: '',
    person_email: '',
    person_phone: '',
    person_company: '',
    photo_url: '',
    vehicle_reg: '',
    attending_site_id: '',
  });
  
  const [photoPreview, setPhotoPreview] = useState(null);
  const [errors, setErrors] = useState({});

  // Load profile and sites on mount — sites must resolve first so Select can
  // match the stored UUID to a label immediately (avoids raw-UUID flash).
  useEffect(() => {
    const email = session?.person?.email;
    if (!email) return;
    loadAll(email);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  const loadAll = async (email) => {
    setLoading(true);
    try {
      // 1. Load sites first so Select options are ready before we set the value
      const { data: siteData } = await supabase
        .from('depots')
        .select('id, name, code')
        .eq('is_active', true)
        .order('name');
      setSites(siteData || []);

      // 2. Load saved profile
      const { data: profile, error: profileError } = await supabase
        .from('portal_profiles')
        .select('*')
        .eq('person_email', email)
        .maybeSingle();

      if (profileError && profileError.code !== 'PGRST116') throw profileError;

      if (profile) {
        const noVeh = !profile.vehicle_reg || profile.vehicle_reg === 'NO_VEHICLE';
        setNoVehicle(noVeh);

        // Always pull the site from the latest invite so a returning user
        // who has been invited to a different site gets the correct site
        // pre-selected rather than their old one.
        const { data: latestInvite } = await supabase
          .from('induction_submissions')
          .select('depot_id')
          .eq('person_email', email)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        // Fallback chain for site: induction submission → saved profile → lead project → team member project
        let resolvedSiteId = latestInvite?.depot_id || profile.attending_site_id || '';
        if (!resolvedSiteId) {
          const { data: leadProject } = await supabase
            .from('projects')
            .select('site_id')
            .eq('project_lead_email', email)
            .order('created_at', { ascending: false })
            .limit(1)
            .maybeSingle();
          resolvedSiteId = leadProject?.site_id || '';
        }
        if (!resolvedSiteId) {
          const { data: memberRow } = await supabase
            .from('project_team_members')
            .select('projects(site_id)')
            .eq('person_email', email)
            .order('created_at', { ascending: false })
            .limit(1)
            .maybeSingle();
          resolvedSiteId = memberRow?.projects?.site_id || '';
        }

        setFormData({
          person_name: profile.person_name || '',
          person_email: profile.person_email || email,
          person_phone: profile.person_phone || '',
          person_company: profile.person_company || '',
          photo_url: profile.photo_url || '',
          vehicle_reg: noVeh ? '' : (profile.vehicle_reg || ''),
          attending_site_id: resolvedSiteId,
        });
        setPhotoPreview(profile.photo_url || null);
      } else {
        // 3. No profile yet — pre-fill from induction_submissions, then project_team_members
        const { data: invite } = await supabase
          .from('induction_submissions')
          .select('person_name, person_company, depot_id')
          .eq('person_email', email)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        // Also check project_team_members — the lead filled in name/company/phone when inviting
        const { data: memberRow } = await supabase
          .from('project_team_members')
          .select('person_name, person_company, person_phone, projects(site_id)')
          .eq('person_email', email)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        // Fallback chain for site: induction submission → lead project → team member project
        let siteId = invite?.depot_id || '';
        if (!siteId) {
          const { data: leadProject } = await supabase
            .from('projects')
            .select('site_id')
            .eq('project_lead_email', email)
            .order('created_at', { ascending: false })
            .limit(1)
            .maybeSingle();
          siteId = leadProject?.site_id || '';
        }
        if (!siteId) {
          siteId = memberRow?.projects?.site_id || '';
        }

        setFormData(prev => ({
          ...prev,
          person_email: email,
          person_name:    invite?.person_name    || memberRow?.person_name    || '',
          person_company: invite?.person_company || memberRow?.person_company || '',
          person_phone:   memberRow?.person_phone || '',
          attending_site_id: siteId,
        }));
      }
    } catch (err) {
      console.error('Error loading profile:', err);
      toast({
        title: 'Error',
        description: 'Failed to load profile data',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.person_name?.trim()) {
      newErrors.person_name = 'Name is required';
    }
    
    if (!formData.person_email?.trim()) {
      newErrors.person_email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.person_email)) {
      newErrors.person_email = 'Please enter a valid email';
    }
    
    if (!formData.person_phone?.trim()) {
      newErrors.person_phone = 'Contact number is required';
    }
    
    if (!formData.person_company?.trim()) {
      newErrors.person_company = 'Company name is required';
    }
    
    if (!formData.attending_site_id) {
      newErrors.attending_site_id = 'Please select a site';
    }
    
    if (!formData.photo_url && !photoPreview) {
      newErrors.photo_url = 'Profile photo is required';
    }
    
    if (!noVehicle && !formData.vehicle_reg?.trim()) {
      newErrors.vehicle_reg = 'Vehicle registration is required (or tick \'No vehicle\')';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFileUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    // Check file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast({
        title: 'File too large',
        description: 'Please upload an image smaller than 2MB',
        variant: 'destructive',
      });
      return;
    }
    
    // Check file type
    if (!file.type.startsWith('image/')) {
      toast({
        title: 'Invalid file type',
        description: 'Please upload an image file',
        variant: 'destructive',
      });
      return;
    }
    
    const reader = new FileReader();
    reader.onload = () => {
      setPhotoPreview(reader.result);
      setFormData(prev => ({ ...prev, photo_url: reader.result }));
    };
    reader.readAsDataURL(file);
  };

  const startCamera = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: 800, height: 800 }
      });
      setStream(mediaStream);
      setShowCamera(true);
      
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = mediaStream;
        }
      }, 100);
    } catch (error) {
      console.error('Camera error:', error);
      toast({
        title: 'Camera Error',
        description: 'Could not access camera. Please check permissions.',
        variant: 'destructive',
      });
    }
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;
    
    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = 800;
    canvas.height = 800;
    
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0, 800, 800);
    
    const photoData = canvas.toDataURL('image/jpeg', 0.8);
    setPhotoPreview(photoData);
    setFormData(prev => ({ ...prev, photo_url: photoData }));
    
    stopCamera();
    toast({
      title: 'Photo Captured',
      description: 'Your profile photo has been captured',
    });
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    setShowCamera(false);
  };

  const uploadPhoto = async (photoDataUrl) => {
    try {
      // Convert base64 to blob
      const response = await fetch(photoDataUrl);
      const blob = await response.blob();
      
      const email = session?.person?.email;
      const timestamp = Date.now();
      const filePath = `portal-photos/${email}/${timestamp}.jpg`;
      
      const { data, error } = await supabase.storage
        .from('company-assets')
        .upload(filePath, blob, {
          contentType: 'image/jpeg',
          upsert: false,
        });
      
      if (error) throw error;
      
      const { data: urlData } = supabase.storage
        .from('company-assets')
        .getPublicUrl(filePath);
      
      return urlData.publicUrl;
    } catch (error) {
      console.error('Photo upload error:', error);
      throw new Error('Failed to upload photo');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast({
        title: 'Validation Error',
        description: 'Please fill in all required fields',
        variant: 'destructive',
      });
      return;
    }
    
    setSaving(true);
    try {
      let photoUrl = formData.photo_url;
      
      // Upload photo if it's a base64 data URL
      if (photoUrl && photoUrl.startsWith('data:')) {
        photoUrl = await uploadPhoto(photoUrl);
      }
      
      const profileData = {
        person_name: formData.person_name,
        person_phone: formData.person_phone,
        person_company: formData.person_company,
        photo_url: photoUrl,
        vehicle_reg: noVehicle ? 'NO_VEHICLE' : formData.vehicle_reg,
        attending_site_id: formData.attending_site_id,
      };
      
      // Call Edge Function to save profile (validates portal token)
      const portalToken = localStorage.getItem('portal_session_token');
      if (!portalToken) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/portal-save-profile`,
        {
          method: 'POST',
          headers: {
            'x-portal-token': portalToken,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(profileData),
        }
      );

      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.error || 'Failed to save profile');
      }
      
      toast({
        title: 'Profile Confirmed',
        description: 'Your details have been confirmed. Proceeding to the next step.',
      });

      // Return to the caller if provided (e.g. invite setup guide), otherwise fall back to welcome
      const returnTo = location.state?.returnTo;
      navigate(returnTo || '/portal/welcome', { state: returnTo ? undefined : { expandStep: 'induction' } });
    } catch (error) {
      console.error('Save error:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to save profile',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-center text-muted-foreground">Loading profile...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title="Complete Your Profile"
        subtitle="All fields are mandatory before you can access inductions"
      />

      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          Please review your details below and confirm they are still correct.
          Even if you have visited before, you must re-confirm your profile for
          each new project invitation before you can access the site induction.
        </AlertDescription>
      </Alert>

      <form onSubmit={handleSubmit}>
        <div className="grid gap-6 md:grid-cols-2">
          {/* Left Column - Photo Upload */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Camera className="h-5 w-5" />
                Profile Photo *
              </CardTitle>
              <CardDescription>
                Upload or capture a clear photo of yourself
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {photoPreview ? (
                <div className="space-y-4">
                  <div className="relative w-48 h-48 mx-auto">
                    <img
                      src={photoPreview}
                      alt="Profile"
                      className="w-full h-full rounded-full object-cover border-4 border-primary"
                    />
                  </div>
                  <div className="flex gap-2 justify-center">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      Change Photo
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={startCamera}
                    >
                      <Camera className="h-4 w-4 mr-2" />
                      Retake
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="w-48 h-48 mx-auto bg-muted rounded-full flex items-center justify-center">
                    <User className="h-24 w-24 text-muted-foreground" />
                  </div>
                  <div className="flex gap-2 justify-center">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      Upload Photo
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={startCamera}
                    >
                      <Camera className="h-4 w-4 mr-2" />
                      Take Photo
                    </Button>
                  </div>
                </div>
              )}
              
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleFileUpload}
              />
              
              {errors.photo_url && (
                <p className="text-sm text-destructive">{errors.photo_url}</p>
              )}
            </CardContent>
          </Card>

          {/* Right Column - Personal Details */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                Personal Details
              </CardTitle>
              <CardDescription>
                Your contact information
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="person_name">
                  <User className="h-4 w-4 inline mr-1" />
                  Full Name *
                </Label>
                <Input
                  id="person_name"
                  value={formData.person_name}
                  onChange={(e) => setFormData({ ...formData, person_name: e.target.value })}
                  placeholder="John Smith"
                  className={errors.person_name ? 'border-destructive' : ''}
                />
                {errors.person_name && (
                  <p className="text-sm text-destructive mt-1">{errors.person_name}</p>
                )}
              </div>

              <div>
                <Label htmlFor="person_email">
                  <Mail className="h-4 w-4 inline mr-1" />
                  Email Address *
                </Label>
                <Input
                  id="person_email"
                  type="email"
                  value={formData.person_email}
                  onChange={(e) => setFormData({ ...formData, person_email: e.target.value })}
                  placeholder="john.smith@example.com"
                  disabled
                  className="bg-muted"
                />
                {errors.person_email && (
                  <p className="text-sm text-destructive mt-1">{errors.person_email}</p>
                )}
              </div>

              <div>
                <Label htmlFor="person_phone">
                  <Phone className="h-4 w-4 inline mr-1" />
                  Contact Number *
                </Label>
                <Input
                  id="person_phone"
                  type="tel"
                  value={formData.person_phone}
                  onChange={(e) => setFormData({ ...formData, person_phone: e.target.value })}
                  placeholder="07700 900000"
                  className={errors.person_phone ? 'border-destructive' : ''}
                />
                {errors.person_phone && (
                  <p className="text-sm text-destructive mt-1">{errors.person_phone}</p>
                )}
              </div>

              <div>
                <Label htmlFor="person_company">
                  <Building2 className="h-4 w-4 inline mr-1" />
                  Company Name *
                </Label>
                <Input
                  id="person_company"
                  value={formData.person_company}
                  onChange={(e) => setFormData({ ...formData, person_company: e.target.value })}
                  placeholder="ABC Construction Ltd"
                  className={errors.person_company ? 'border-destructive' : ''}
                />
                {errors.person_company && (
                  <p className="text-sm text-destructive mt-1">{errors.person_company}</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Site Details - Full Width */}
          <Card className="md:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="h-5 w-5" />
                Site & Vehicle Details
              </CardTitle>
              <CardDescription>
                Select the site you'll be attending and provide vehicle information
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <Label htmlFor="attending_site_id">
                    <MapPin className="h-4 w-4 inline mr-1" />
                    Site Attending *
                  </Label>
                  <Select
                    value={formData.attending_site_id}
                    onValueChange={(value) => setFormData({ ...formData, attending_site_id: value })}
                  >
                    <SelectTrigger className={errors.attending_site_id ? 'border-destructive' : ''}>
                      <SelectValue placeholder="Select a site">
                        {formData.attending_site_id && (() => {
                          const s = sites.find(s => s.id === formData.attending_site_id);
                          return s ? `${s.name} (${s.code})` : null;
                        })()}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {sites.map((site) => (
                        <SelectItem key={site.id} value={site.id}>
                          {site.name} ({site.code})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.attending_site_id && (
                    <p className="text-sm text-destructive mt-1">{errors.attending_site_id}</p>
                  )}
                </div>

                <div>
                  <Label htmlFor="vehicle_reg">
                    <Car className="h-4 w-4 inline mr-1" />
                    Vehicle Registration {!noVehicle && '*'}
                  </Label>
                  <Input
                    id="vehicle_reg"
                    value={noVehicle ? '' : formData.vehicle_reg}
                    onChange={(e) => setFormData({ ...formData, vehicle_reg: e.target.value.toUpperCase() })}
                    placeholder={noVehicle ? 'No vehicle — on foot / lift share' : 'AB12 CDE'}
                    className={errors.vehicle_reg ? 'border-destructive' : ''}
                    disabled={noVehicle}
                    maxLength={10}
                  />
                  <div className="flex items-center gap-2 mt-2">
                    <input
                      type="checkbox"
                      id="no_vehicle"
                      checked={noVehicle}
                      onChange={(e) => {
                        setNoVehicle(e.target.checked);
                        if (e.target.checked) setFormData(prev => ({ ...prev, vehicle_reg: '' }));
                        if (errors.vehicle_reg) setErrors(prev => ({ ...prev, vehicle_reg: undefined }));
                      }}
                      className="h-4 w-4 rounded border-slate-300 accent-amber-500"
                    />
                    <label htmlFor="no_vehicle" className="text-sm text-slate-600 cursor-pointer select-none">
                      No vehicle (on foot / lift share)
                    </label>
                  </div>
                  {errors.vehicle_reg && (
                    <p className="text-sm text-destructive mt-1">{errors.vehicle_reg}</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Submit Button */}
        <div className="mt-6 flex justify-end gap-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/portal/dashboard')}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={saving}>
            {saving ? (
              'Confirming...'
            ) : (
              <>
                <Check className="h-4 w-4 mr-2" />
                Confirm & Continue
              </>
            )}
          </Button>
        </div>
      </form>

      {/* Camera Modal */}
      {showCamera && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <div className="bg-background rounded-lg p-6 max-w-2xl w-full">
            <h3 className="text-lg font-semibold mb-4">Take Your Photo</h3>
            <div className="relative">
              <video
                ref={videoRef}
                autoPlay
                playsInline
                className="w-full rounded-lg"
              />
              <canvas ref={canvasRef} className="hidden" />
            </div>
            <div className="flex gap-4 mt-4 justify-end">
              <Button type="button" variant="outline" onClick={stopCamera}>
                Cancel
              </Button>
              <Button type="button" onClick={capturePhoto}>
                <Camera className="h-4 w-4 mr-2" />
                Capture Photo
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PortalProfile;
